// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The data layer for REVIEW-BEFORE-SEND (the retail-newsletter content review).
//
// This is the frontend half of the control designed in
// docs/investment-reporting/0003i_newsletter_content_review.sql (DRAFT — NOT APPLIED).
// The server half is the wall; everything here is a surface that lets a named human SEE the
// exact bytes before attesting to them, and that refuses honestly when it cannot.
//
// Four things live here:
//
//   * useNewsletterReviewState  — reporting_newsletter_review_state(_cycle_id) -> jsonb.
//     The single read shape: newest frozen newsletter version, its object coordinates, the
//     pinned send envelope, the newest review/withdrawal row, the SERVER-OWNED checklist, and
//     reasons[] explaining why the send is currently refused. `reviewed` is delegated
//     server-side to the fail-closed predicate (0003i §6:530) so the UI and the wall cannot
//     disagree — this hook NEVER computes `reviewed` itself.
//
//   * useNewsletterPreview      — fetch the frozen HTML bytes and verify them client-side.
//     This is the one genuinely client-side assertion in the whole island: sha256 of what the
//     browser actually received, compared to the hash the review row will name. The server
//     cannot make this assertion for us — it is the entire point of "the human saw THESE bytes".
//
//   * useRecordNewsletterContentReview / useRevokeNewsletterContentReview — the two write acts.
//
// THREE HONEST-REFUSAL STATES, none of which may be papered over with a blank or a fake:
//   (a) rpcMissing      — 0003i has not been applied; the review RPCs do not exist. The surface
//                         says so and names the file, rather than erroring.
//   (b) has_artifact=false — no frozen newsletter has ever existed (both storage buckets are
//                         empty; stageRender throws on the missing template). Nothing to review.
//   (c) preview unavailable — the frozen row exists but its bytes cannot be displayed, because
//                         there is no service-role signer to convert the sign_reporting_artifact
//                         GRANT (jsonb, 0003c:362-406 -> 0003b:443-502) into a Storage URL, and
//                         0003b:429-431 deliberately declines a storage.objects policy. We say
//                         exactly that and DISABLE the review — we never render an empty frame
//                         and call it "reviewed".
//
// FAIL-CLOSED ON THE CLIENT TOO: if the bytes cannot be fetched, or cannot be hashed (no
// WebCrypto), or hash to something other than the named artifact_sha256, the review act is
// blocked with no override. Unlike GateTab's advisory artifact mirror (GateTab.tsx:164-176)
// this check cannot drift more-restrictive-than-server in a way that locks anyone out of a
// decision the server would allow — it is an integrity assertion about the bytes on screen,
// and if it fails, the thing on screen is not the thing that would be sent.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// The service-role signer contemplated by the design note §4d. It is NOT deployed today;
// when it is absent this module degrades to an explicit "cannot display" state (never a blank).
const ARTIFACT_FETCH_FN = "reporting-artifact-fetch";

// Refuse to inline anything absurd into an iframe srcdoc. A performance-monitor email is
// tens of KB; anything past this is a signal something is wrong, not a rendering challenge.
const MAX_PREVIEW_BYTES = 5 * 1024 * 1024;

// ============================================================================
// Types — the jsonb shapes 0003i §6 returns. Permissive (server owns the shape).
// ============================================================================

export interface NewsletterSendEnvelope {
  subject?: string | null;
  from_name?: string | null;
  reply_to?: string | null;
  audience_id?: string | null;
  [k: string]: unknown;
}

export interface NewsletterReviewRow {
  action?: "review_content" | "revoke_content_review" | string | null;
  actor?: string | null;
  actor_team?: string | null;
  at?: string | null;
  note?: string | null;
  checklist?: Record<string, unknown> | null;
  ir_hash?: string | null;
  envelope_sha256?: string | null;
}

export interface NewsletterReviewState {
  /** SERVER-computed, delegated to the fail-closed predicate. Never recomputed client-side. */
  reviewed: boolean;
  has_artifact: boolean;
  artifact_sha256?: string | null;
  ir_sha256?: string | null;
  current_ir_sha256?: string | null;
  bytes?: number | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
  content_type?: string | null;
  send_envelope?: NewsletterSendEnvelope | null;
  envelope_sha256?: string | null;
  delivered_at?: string | null;
  review?: NewsletterReviewRow | null;
  /** The server-owned definition of a complete review. A shorter client list cannot pass. */
  checklist?: string[];
  reasons?: string[];
}

export interface NewsletterReviewStateResult {
  /** true when 0003i has not been applied — the review RPCs do not exist in this database. */
  rpcMissing: boolean;
  state: NewsletterReviewState | null;
}

export const NEWSLETTER_REVIEW_STATE_KEY = "reporting_newsletter_review_state";
export const NEWSLETTER_PREVIEW_KEY = "reporting_newsletter_preview";

// The names 0003i creates. Used to keep the missing-capability test NARROW — see below.
const REVIEW_FN_NAMES = [
  "reporting_newsletter_review_state",
  "record_newsletter_content_review",
  "revoke_newsletter_content_review",
  "reporting_newsletter_content_reviewed",
];

/**
 * Is this error "the 0003i control is not installed" rather than a genuine failure?
 *
 * VERIFIED LIVE 2026-08-11 against ewyfhrjexfrmcjhqzsmm: calling the RPC exactly as this hook
 * does returns HTTP 404 with
 *   {"code":"PGRST202","message":"Could not find the function
 *    public.reporting_newsletter_review_state(_cycle_id) in the schema cache", ...}
 * PGRST202 is emitted by PostgREST only when it cannot resolve the function at all, so it is
 * an unambiguous "not installed" signal and is accepted on its own.
 *
 * DELIBERATELY NARROW everywhere else. An earlier version also accepted any 42883 and
 * regex-matched /function .* does not exist/ on the message — that over-swallows: if 0003i IS
 * applied but its body calls some other missing helper, Postgres raises 42883 from INSIDE a
 * function that exists, and reporting "the control is not installed" would name the wrong
 * cause and hide a real broken deployment. So 42883 counts only when the message actually
 * names one of the 0003i functions. Every other error propagates and surfaces as an error.
 */
export function isMissingReviewCapability(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  if (!e) return false;
  if (e.code === "PGRST202") return true;
  if (e.code === "42883") {
    const m = String(e.message ?? "").toLowerCase();
    return REVIEW_FN_NAMES.some((n) => m.includes(n));
  }
  return false;
}

/**
 * Coerce a server-controlled jsonb field to a string[].
 *
 * `checklist` and `reasons` cross the wire as untyped jsonb. The tab calls .every()/.map() on
 * them, and there is NO error boundary anywhere in src/ (verified 2026-08-11), so if the server
 * ever returned an object or a scalar where an array was expected, the resulting TypeError
 * would unmount the whole SPA — not just this tab. Normalising here keeps that impossible.
 *
 * This is not papering over a failure: an empty checklist makes `allTicked` false, so the
 * review stays BLOCKED and the tab says the definition of "complete" is missing.
 */
function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string");
}

/** Normalise the jsonb the RPC returns into the shape the tab can safely render. */
function normaliseState(data: unknown): NewsletterReviewState | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const raw = data as Record<string, unknown>;
  return {
    ...(raw as unknown as NewsletterReviewState),
    // Never trust these two to be arrays — the tab iterates both.
    checklist: asStringArray(raw.checklist),
    reasons: asStringArray(raw.reasons),
    // Booleans the tab branches on, forced to real booleans.
    reviewed: raw.reviewed === true,
    has_artifact: raw.has_artifact === true,
  };
}

// ============================================================================
// 1. Read state
// ============================================================================
export function useNewsletterReviewState(cycleId: string | undefined) {
  return useQuery<NewsletterReviewStateResult>({
    queryKey: [NEWSLETTER_REVIEW_STATE_KEY, cycleId],
    enabled: !!cycleId,
    // The RPC does not exist until 0003i is applied; do not hammer a 404 three times.
    retry: false,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc(NEWSLETTER_REVIEW_STATE_KEY, {
        _cycle_id: cycleId,
      });
      if (error) {
        if (isMissingReviewCapability(error)) return { rpcMissing: true, state: null };
        // Anything else is a GENUINE failure — a permission refusal, a network fault, a bug.
        // It must reach the error state, not be dressed up as "not installed yet".
        throw error;
      }
      return { rpcMissing: false, state: normaliseState(data) };
    },
  });
}

// ============================================================================
// 2. Preview + client-side integrity
// ============================================================================

export type PreviewUnavailableReason =
  | "no_artifact" // nothing frozen for this cycle
  | "no_object" // the frozen row names no stored object
  | "signer_missing" // no service-role signer / no storage policy — bytes cannot be reached
  | "too_large"
  | "fetch_failed"
  | "no_webcrypto"; // cannot hash, so cannot assert what is on screen

export interface NewsletterPreview {
  html: string | null;
  /** sha256 of the bytes THIS BROWSER received. */
  computedSha256: string | null;
  /** sha256 the frozen row / review row names. */
  expectedSha256: string | null;
  /** true only when both hashes exist and are equal. Anything else blocks the review. */
  integrityOk: boolean;
  unavailable: PreviewUnavailableReason | null;
  /** Plain-English detail for the surface. Never a raw stack. */
  detail: string | null;
}

function emptyPreview(
  unavailable: PreviewUnavailableReason | null,
  detail: string | null,
  expectedSha256: string | null = null,
): NewsletterPreview {
  return { html: null, computedSha256: null, expectedSha256, integrityOk: false, unavailable, detail };
}

async function sha256Hex(buf: ArrayBuffer): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const digest = await subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Fetch the frozen newsletter HTML and hash it.
 *
 * Path 1 (preferred, design note §4d): the `reporting-artifact-fetch` edge function verifies the
 * caller's JWT, calls sign_reporting_artifact AS THE CALLER (so can_review_reporting applies) and
 * uses service-role ONLY to mint the Storage signed URL. NOT DEPLOYED today.
 *
 * Path 2 (fallback): direct client signing via storage.from(bucket).createSignedUrl — works only
 * if the storage.objects SELECT policy that 0003b:429-431 deliberately declines has since been
 * added. Attempted only when path 1 is absent, so the surface still works if the owner picks the
 * cheaper option.
 *
 * NEVER throws. Every failure becomes an explicit, nameable unavailable state.
 */
export function useNewsletterPreview(
  cycleId: string | undefined,
  state: NewsletterReviewState | null | undefined,
) {
  const artifactSha = state?.artifact_sha256 ?? null;
  const bucket = state?.storage_bucket ?? "reporting-artifacts";
  const path = state?.storage_path ?? null;

  return useQuery<NewsletterPreview>({
    queryKey: [NEWSLETTER_PREVIEW_KEY, cycleId, artifactSha],
    enabled: !!cycleId && !!state?.has_artifact,
    retry: false,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!state?.has_artifact || !artifactSha)
        return emptyPreview("no_artifact", "No frozen newsletter artifact exists for this cycle.");
      if (!path)
        return emptyPreview(
          "no_object",
          "The frozen newsletter row names no stored object, so there are no bytes to display.",
          artifactSha,
        );
      if (!globalThis.crypto?.subtle)
        return emptyPreview(
          "no_webcrypto",
          "This browser exposes no WebCrypto SubtleCrypto (a non-secure context?), so the displayed bytes cannot be verified against the frozen hash.",
          artifactSha,
        );
      if (state.bytes != null && state.bytes > MAX_PREVIEW_BYTES)
        return emptyPreview(
          "too_large",
          `The frozen artifact is ${state.bytes} bytes — too large to inline for review.`,
          artifactSha,
        );

      let buf: ArrayBuffer | null = null;
      let signerDetail: string | null = null;

      // --- path 1: the service-role signer edge function -------------------------
      try {
        const { data, error } = await supabase.functions.invoke(ARTIFACT_FETCH_FN, {
          body: { cycle_id: cycleId, kind: "newsletter", artifact_sha256: artifactSha },
        });
        if (error) {
          const status = (error as any)?.context?.status;
          signerDetail =
            status === 404
              ? `The ${ARTIFACT_FETCH_FN} edge function is not deployed.`
              : `The ${ARTIFACT_FETCH_FN} edge function refused: ${(error as any)?.message ?? "unknown error"}.`;
        } else {
          const body = (data ?? {}) as { html?: string; signed_url?: string };
          if (typeof body.html === "string") {
            buf = new TextEncoder().encode(body.html).buffer as ArrayBuffer;
          } else if (typeof body.signed_url === "string" && body.signed_url) {
            const res = await fetch(body.signed_url);
            if (!res.ok)
              return emptyPreview(
                "fetch_failed",
                `The signed URL returned HTTP ${res.status}.`,
                artifactSha,
              );
            buf = await res.arrayBuffer();
          } else {
            signerDetail = `${ARTIFACT_FETCH_FN} returned neither html nor signed_url.`;
          }
        }
      } catch (e: any) {
        signerDetail = `Could not reach ${ARTIFACT_FETCH_FN}: ${e?.message ?? "network error"}.`;
      }

      // --- path 2: direct client signing (only if the storage policy now exists) ---
      if (!buf) {
        try {
          const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 300);
          if (!error && data?.signedUrl) {
            const res = await fetch(data.signedUrl);
            if (res.ok) buf = await res.arrayBuffer();
          }
        } catch {
          // swallow — the honest signer_missing state below is the answer
        }
      }

      if (!buf)
        return emptyPreview(
          "signer_missing",
          `${signerDetail ?? "No signer available."} There is also no storage.objects read policy on the ${bucket} bucket (0003b:429-431 declines one deliberately), so the client cannot sign the object itself. The rendered copy cannot be displayed, and a review cannot honestly be recorded against bytes nobody can see.`,
          artifactSha,
        );

      if (buf.byteLength > MAX_PREVIEW_BYTES)
        return emptyPreview("too_large", `Fetched ${buf.byteLength} bytes — too large to inline.`, artifactSha);

      const computed = await sha256Hex(buf);
      const html = new TextDecoder("utf-8").decode(buf);
      const integrityOk = !!computed && !!artifactSha && computed === artifactSha;

      return {
        html,
        computedSha256: computed,
        expectedSha256: artifactSha,
        integrityOk,
        unavailable: null,
        detail: integrityOk
          ? null
          : "The bytes this browser fetched do NOT hash to the artifact_sha256 the frozen row names. What is on screen is not what would be sent — the review is blocked.",
      };
    },
  });
}

// ============================================================================
// 3. Write acts
// ============================================================================

function invalidateReview(qc: ReturnType<typeof useQueryClient>, cycleId: string) {
  qc.invalidateQueries({ queryKey: [NEWSLETTER_REVIEW_STATE_KEY, cycleId] });
  qc.invalidateQueries({ queryKey: ["get_reporting_cycle", cycleId] });
  qc.invalidateQueries({ queryKey: ["list_reporting_artifacts", cycleId] });
}

/**
 * record_newsletter_content_review(_cycle_id,_artifact_sha256,_checklist,_note).
 *
 * The client supplies ONLY the checklist ticks and the note. Every binding fact — ir_hash,
 * envelope_sha256, the version id, the tier — is stamped server-side from the frozen row
 * (0003i §3). A disabled button here is UX; the RPC re-derives and refuses on any doubt.
 */
export function useRecordNewsletterContentReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      artifactSha256,
      checklist,
      note,
    }: {
      cycleId: string;
      artifactSha256: string;
      /** every key from the SERVER checklist, each true */
      checklist: Record<string, boolean>;
      note?: string | null;
    }) => {
      const { data, error } = await (supabase as any).rpc("record_newsletter_content_review", {
        _cycle_id: cycleId,
        _artifact_sha256: artifactSha256,
        _checklist: checklist,
        _note: note ?? null,
      });
      if (error) throw error;
      return data as Record<string, any>;
    },
    onSuccess: (_d, vars) => invalidateReview(qc, vars.cycleId),
  });
}

/**
 * revoke_newsletter_content_review(_cycle_id,_artifact_sha256,_note).
 * Withdrawal is a NEW append-only row, never a delete (0003i §4).
 */
export function useRevokeNewsletterContentReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      artifactSha256,
      note,
    }: {
      cycleId: string;
      artifactSha256: string;
      note?: string | null;
    }) => {
      const { data, error } = await (supabase as any).rpc("revoke_newsletter_content_review", {
        _cycle_id: cycleId,
        _artifact_sha256: artifactSha256,
        _note: note ?? null,
      });
      if (error) throw error;
      return data as Record<string, any>;
    },
    onSuccess: (_d, vars) => invalidateReview(qc, vars.cycleId),
  });
}
